import { type MouseEvent } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Chip,
  Menu,
  MenuItem,
  type SxProps,
  type Theme,
} from '@mui/material';
import { useMenu } from '@core/hooks/useMenu';
import type { MaterialSwatchPaletteKey } from '@core/theme/theme';

export interface DropdownChipOption<T extends string = string> {
  value: T;
  label: string;
}

export interface DropdownChipProps<T extends string = string> {
  label: string;
  color?: MaterialSwatchPaletteKey;
  options: DropdownChipOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

const DropdownChip = <T extends string>({
  label,
  color,
  options,
  onChange,
  disabled = false,
  sx,
}: DropdownChipProps<T>) => {
  const { anchorEl, isOpen, openMenu, closeMenu } = useMenu();
  const interactive = !disabled && options.length > 0;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!interactive) return;
    openMenu(event);
  };

  const handleSelect = (option: DropdownChipOption<T>) => {
    closeMenu();
    onChange(option.value);
  };

  return (
    <>
      <Chip
        size="small"
        label={
          <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
          >
            {label}
            {interactive ? (
              <ArrowDropDownIcon sx={{ fontSize: 16, opacity: 0.8 }} />
            ) : null}
          </Box>
        }
        onClick={interactive ? handleOpen : undefined}
        sx={[
          {
            fontWeight: 'bold',
            letterSpacing: '0.25px',
            cursor: interactive ? 'pointer' : 'default',
          },
          ...(color
            ? [
                {
                  color: `${color}.900`,
                  bgcolor: `${color}.100`,
                  '&.MuiChip-clickable:hover, &.MuiChip-clickable.MuiChip-focusVisible':
                    {
                      bgcolor: `${color}.200`,
                    },
                },
              ]
            : []),
          ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
        ]}
      />
      {interactive ? (
        <Menu
          open={isOpen}
          onClose={closeMenu}
          anchorEl={anchorEl}
          slotProps={{
            root: { onClick: (event: MouseEvent) => event.stopPropagation() },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} onClick={() => handleSelect(option)}>
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </>
  );
};

export default DropdownChip;
